import {
  type RefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { Assert } from '@team/assert';
import { ArrayKit } from '@team/array';
import { Dom } from '@team/dom';
import { NumberKit } from '@team/number';
import { type SafeAreaInsets } from './types';
import { setSafeAreaInsets } from './model';

type Params<T> = {
  insets: Maybe<SafeAreaInsets>;
  customPropertyPrefix: string;
  targetRef: RefObject<T>;
};

export function useSafeAreaInsetsSetup<T extends HTMLElement>({
  insets,
  customPropertyPrefix,
  targetRef,
}: Params<T>) {
  useLayoutEffect(() => {
    if (Assert.isUndefined(insets)) {
      return;
    }

    if (Assert.isNull(targetRef.current)) {
      return;
    }

    const bodyElement = Dom.getDocumentBody(targetRef.current);
    const htmlElement = bodyElement.ownerDocument.documentElement;

    const unsetSafeAreaInsets = setSafeAreaInsets(
      insets,
      customPropertyPrefix,
      htmlElement
    );

    return unsetSafeAreaInsets;
  }, [insets, customPropertyPrefix, targetRef]);
}

export function useContentSafeAreaInsets(input?: SafeAreaInsets) {
  const [value, setValue] = useState<SafeAreaInsets | undefined>(input);
  const registry = useRef(new Map<string, SafeAreaInsets>());

  const compose = useCallback((): SafeAreaInsets | undefined => {
    const values = Array.from(registry.current.values()).filter(
      Assert.isDefined
    );

    if (ArrayKit.isEmpty(values)) {
      return undefined;
    }

    const insets = values.reduce<Required<SafeAreaInsets>>(
      (acc, { top = 0, right = 0, bottom = 0, left = 0 }) => {
        acc.top += top;
        acc.right += right;
        acc.bottom += bottom;
        acc.left += left;

        return acc;
      },
      {
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
      }
    );

    if (!Object.values(insets).some(NumberKit.isPositive)) {
      return undefined;
    }

    return insets;
  }, []);

  const register = useCallback(
    (id: string, value: SafeAreaInsets) => {
      registry.current.set(id, value);

      setValue(compose());
    },
    [compose]
  );

  const unregister = useCallback(
    (id: string) => {
      registry.current.delete(id);

      setValue(compose());
    },
    [compose]
  );

  useEffect(() => {
    if (Assert.isUndefined(input)) {
      return;
    }

    register('external', input);

    return () => {
      unregister('external');
    };
  }, [input, register, unregister]);

  return {
    value,
    register,
    unregister,
  };
}
